/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode } from 'react';

import Indent from '@totem/components/ui_components/layout/Indent';
import {
  getUserRole,
  hasAnyActiveService,
  isMemberOfAny,
  OrganizationHasApiSiteType,
  securityGroupCheckConstraintAny,
  securityGroupCheckConstraintEmpty,
} from '@totem/components/userContext/security/userUtilities';
import { ContractedServices } from '@totem/types/contractedServices';
import { SecurityGroupCheckConstraints, UserProfile } from '@totem/types/user';
import { isNotNull } from '@totem/utilities/common';
import { isIBUser } from '@totem/utilities/security';

export const permissions: Permissions = {
  events: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Threat Management" service. <br />
        AND (<br />
        <Indent size={16}>
          Role must be "System Administrator" or better.
          <br />
          OR
          <br />
          Member of ("Event Viewer" or "Event Manager") group.
          <br />
        </Indent>
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        hasAnyActiveService(userProfile, [
          'threat_management',
          'threat_detection',
          'server_resilience',
        ]) &&
        (permissions.systemAdministrator.hasPermissions(userProfile) ||
          isMemberOfAny(
            userProfile,
            ['event_viewer', 'event_manager'],
            isNotNull(constraints)
              ? constraints
              : securityGroupCheckConstraintEmpty,
          ));

      return hasAccess;
    },
  },
  eventManager: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "System Administrator" or better.
        <br />
        OR
        <br />
        Member of ("Event Manager") group.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.systemAdministrator.hasPermissions(userProfile) ||
        isMemberOfAny(
          userProfile,
          ['event_manager'],
          isNotNull(constraints)
            ? constraints
            : securityGroupCheckConstraintEmpty,
        );

      return hasAccess;
    },
  },
  devices: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Threat Management", "RMM Server", "RMM Workstation" or
        "Software Reporting" service.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.collaborator.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, [
          'threat_management',
          'threat_detection',
          'asset_inventory',
          'rmm_server',
          'rmm_workstation',
          'rmm',
          'server_resilience',
          'software-reporting',
        ]);
      return hasAccess;
    },
  },
  assetInventory: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Asset Inventory" service.
        <br />
        AND
        <br />
        Role must be "Collaborator" or better.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.collaborator.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, [
          'asset_inventory',
          'inventory_and_threat_detection',
        ]);

      return hasAccess;
    },
  },
  continuity: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Backup" service.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.collaborator.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, ['backup', 'server_resilience']);
      return hasAccess;
    },
  },
  optimize: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Optimize" service.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.collaborator.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, ['intellinet_optimize']);
      return hasAccess;
    },
  },
  software: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "RMM Server", "RMM Workstation" or "Software Reporting"
        service.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.collaborator.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, [
          'rmm_server',
          'rmm_workstation',
          'server_resilience',
          'rmm',
          'software-reporting',
        ]);
      return hasAccess;
    },
  },
  softwareReporting: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Software Reporting" service.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.collaborator.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, ['software-reporting']);
      return hasAccess;
    },
  },
  survey: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Policy Compliance" service.
        <br />
        AND (<br />
        <Indent size={16}>
          Role must be "Collaborator" or better.
          <br />
        </Indent>
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        hasAnyActiveService(userProfile, ['policy_compliance']) &&
        permissions.collaborator.hasPermissions(userProfile);
      return hasAccess;
    },
  },
  remoteAccess: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Remote Access" service.
        <br />
        AND (<br />
        <Indent size={16}>
          Role must be "System Administrator" or better.
          <br />
          OR
          <br />
          Member of ("Remote Access Viewer", "Remote Access Admin" or "Remote
          Access Configuration Admin") group.
          <br />
        </Indent>
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        hasAnyActiveService(userProfile, ['remote_access']) &&
        (permissions.systemAdministrator.hasPermissions(userProfile) ||
          isMemberOfAny(
            userProfile,
            [
              'remote_access_viewer',
              'remote_access_admin',
              'remote_access_configuration_admin',
            ],
            isNotNull(constraints)
              ? constraints
              : securityGroupCheckConstraintEmpty,
          ));
      return hasAccess;
    },
  },
  remoteAccessAdmin: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of "Remote Access Admin" group.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = isMemberOfAny(
        userProfile,
        ['remote_access_admin'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
      return hasAccess;
    },
  },
  remoteAccessOverlayNetworkAdmin: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of "Remote Access Overlay Network Admin" group.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = isMemberOfAny(
        userProfile,
        ['remote_access_overlay_network_admin'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
      return hasAccess;
    },
  },
  remoteAccessConfigurationAdmin: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of "Remote Access Configuration Admin" group.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = isMemberOfAny(
        userProfile,
        ['remote_access_configuration_admin'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
      return hasAccess;
    },
  },
  ticketingInternal: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        User must have "@intelligentbuildings.com" email address. <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return permissions.internal.hasPermissions(userProfile);
    },
  },
  ticketingViewer: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "System Administrator" or better.
        <br />
        OR
        <br />
        Member of ("Ticket Viewer", "Ticket Updater", "Ticket Creator", "Ticket
        Technician" or "Ticket Administrator") group.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return (
        isMemberOfAny(
          userProfile,
          [
            'ticket_viewer',
            'ticket_updater',
            'ticket_creator',
            'ticket_technician',
            'ticket_administrator',
          ],
          isNotNull(constraints)
            ? constraints
            : securityGroupCheckConstraintEmpty,
        ) || permissions.systemAdministrator.hasPermissions(userProfile)
      );
    },
  },
  ticketingUpdater: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "System Administrator" or better.
        <br />
        OR
        <br />
        Member of ("Ticket Updater", "Ticket Creator", "Ticket Technician" or
        "Ticket Administrator") group.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return (
        isMemberOfAny(
          userProfile,
          [
            'ticket_updater',
            'ticket_creator',
            'ticket_technician',
            'ticket_administrator',
          ],
          isNotNull(constraints)
            ? constraints
            : securityGroupCheckConstraintEmpty,
        ) || permissions.systemAdministrator.hasPermissions(userProfile)
      );
    },
  },
  ticketingCreator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of ("Ticket Creator", or "Ticket Administrator") group.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return isMemberOfAny(
        userProfile,
        ['ticket_creator', 'ticket_administrator'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
    },
  },
  ticketingAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "System Administrator" or better.
        <br />
        OR
        <br />
        Member of ("Ticket Administrator") group.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return (
        isMemberOfAny(
          userProfile,
          ['ticket_administrator'],
          isNotNull(constraints)
            ? constraints
            : securityGroupCheckConstraintEmpty,
        ) || permissions.systemAdministrator.hasPermissions(userProfile)
      );
    },
  },
  internal: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        User must have "@intelligentbuildings.com" email address. <br />
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        isIBUser(userProfile) &&
        !permissions.vendor.hasPermissions(userProfile);
      return hasAccess;
    },
  },
  ibOrganization: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Must be in the "Intelligent Buildings" organization.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const isIBOrg =
        userProfile.organization.id === '5b0cf2f679e1b20fd9bb4993' ||
        userProfile.organization.id === '5b2f1be53911bd000175043a';
      return isIBOrg;
    },
  },
  internalIBOrg: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Must be in the "Intelligent Buildings" organization.
        <br />
        AND
        <br />
        User must have "@intelligentbuildings.com" email address. <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return (
        permissions.ibOrganization.hasPermissions(userProfile) &&
        permissions.internal.hasPermissions(userProfile)
      );
    },
  },
  internalIBOrgContractManagement: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Must be in the "Intelligent Buildings" organization.
        <br />
        AND
        <br />
        User must have "@intelligentbuildings.com" email address. <br />
        AND
        <br />
        Member of ("Contract Creator") group.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return (
        permissions.ibOrganization.hasPermissions(userProfile) &&
        permissions.internal.hasPermissions(userProfile) &&
        isMemberOfAny(
          userProfile,
          ['contract_creator'],
          isNotNull(constraints)
            ? constraints
            : securityGroupCheckConstraintEmpty,
        )
      );
    },
  },
  internalRMM: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        User must have "@intelligentbuildings.com" email address. <br />
        AND
        <br />
        Organization has "RMM Server" or "RMM Workstation" service.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.internal.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, [
          'rmm_server',
          'rmm_workstation',
          'rmm',
          'server_resilience',
        ]);
      return hasAccess;
    },
  },
  systemAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "System Administrator" or better.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = userProfile.organization.role >= 3;
      return hasAccess;
    },
  },
  internalSystemAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "System Administrator" or better.
        <br />
        AND
        <br />
        User must have "@intelligentbuildings.com" email address. <br />
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.systemAdministrator.hasPermissions(userProfile) &&
        permissions.internal.hasPermissions(userProfile);
      return hasAccess;
    },
  },
  internalSystemAdministratorIBOrg: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "System Administrator" or better.
        <br />
        AND
        <br />
        User must have "@intelligentbuildings.com" email address. <br />
        AND
        <br />
        Must be in the "Intelligent Buildings" organization.
        <br />
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.ibOrganization.hasPermissions(userProfile) &&
        permissions.systemAdministrator.hasPermissions(userProfile) &&
        permissions.internal.hasPermissions(userProfile);
      return hasAccess;
    },
  },
  billingAdministratorIBOrg: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of ("Billing Administrator") group.
        <br />
        AND
        <br />
        Must be in the "Intelligent Buildings" organization.
        <br />
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const isIBOrg = permissions.ibOrganization.hasPermissions(userProfile);

      const hasAccess = isMemberOfAny(
        userProfile,
        ['billing_administrator'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
      return isIBOrg && hasAccess;
    },
  },
  organizationalAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "Organization Administrator" or better.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = userProfile.organization.role >= 2;
      return hasAccess;
    },
  },
  vendor: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "Vendor".
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = userProfile.organization.role === -1;
      return hasAccess;
    },
  },
  collaborator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "Collaborator" or better.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = userProfile.organization.role >= 0;
      return hasAccess;
    },
  },
  teamMember: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "Team Member" or better.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = userProfile.organization.role >= 1;
      return hasAccess;
    },
  },
  internalOrganizationalAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "Organization Administrator" or better.
        <br />
        AND
        <br />
        User must have "@intelligentbuildings.com" email address. <br />
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.organizationalAdministrator.hasPermissions(userProfile) &&
        permissions.internal.hasPermissions(userProfile);
      return hasAccess;
    },
  },
  rmm: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "RMM Server" or "RMM Workstation" service.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.collaborator.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, [
          'rmm_server',
          'rmm_workstation',
          'rmm',
          'server_resilience',
        ]);
      return hasAccess;
    },
  },
  assessments: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "Cybersecurity Assessment" service.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.collaborator.hasPermissions(userProfile) &&
        hasAnyActiveService(userProfile, ['cybersecurity_assessment']);
      return hasAccess;
    },
  },
  maintenanceWindowViewer: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "Team Member" or better.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = permissions.teamMember.hasPermissions(userProfile);
      return hasAccess;
    },
  },
  maintenanceWindowEditor: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "Team Member" or better.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = permissions.teamMember.hasPermissions(userProfile);
      return hasAccess;
    },
  },
  contractsEditor: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of ("Contract Creator") group.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return isMemberOfAny(
        userProfile,
        ['contract_creator'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
    },
  },
  contractsViewer: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of ("Contract Creator" or "Contract Viewer") group.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return isMemberOfAny(
        userProfile,
        ['contract_creator', 'contract_viewer'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
    },
  },
  documentsAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of ("Documents Administrator") group.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return isMemberOfAny(
        userProfile,
        ['documents_admin'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
    },
  },
  threatDetectionConfigurationAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Member of "Threat Detection Configuration Administrator" group.
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess = isMemberOfAny(
        userProfile,
        ['threat_detection_configuration_admin'],
        isNotNull(constraints)
          ? constraints
          : securityGroupCheckConstraintEmpty,
      );
      return hasAccess;
    },
  },
  configurationAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be &quot;System Administrator&quot; or better.
        <br />
        AND
        <br />
        User must have &quot;@intelligentbuildings.com&quot; email address.
        <br />
        AND
        <br />
        Must be in the &quot;Intelligent Buildings&quot; organization.
        <br />
        AND
        <br />
        Member of (&quot;Configuration Administrator&quot;) group.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return (
        permissions.internalSystemAdministratorIBOrg.hasPermissions(
          userProfile,
        ) &&
        isMemberOfAny(
          userProfile,
          ['configuration_administrator'],
          isNotNull(constraints)
            ? constraints
            : securityGroupCheckConstraintEmpty,
        )
      );
    },
  },
  ruleAdministrator: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be &quot;System Administrator&quot; or better.
        <br />
        AND
        <br />
        User must have &quot;@intelligentbuildings.com&quot; email address.
        <br />
        AND
        <br />
        Must be in the &quot;Intelligent Buildings&quot; organization.
        <br />
        AND
        <br />
        Member of (&quot;Rule Administrator&quot;) group.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return (
        permissions.internalSystemAdministrator.hasPermissions(userProfile) &&
        isMemberOfAny(
          userProfile,
          ['rule_administrator'],
          isNotNull(constraints)
            ? constraints
            : securityGroupCheckConstraintEmpty,
        )
      );
    },
  },
  ruleViewer: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        User must have &quot;@intelligentbuildings.com&quot; email address.
        <br />
        AND (
        <br />
        <Indent size={16}>
          Role must be &quot;System Administrator&quot; or better.
          <br />
          OR
          <br />
          Member of (&quot;Rule Administrator&quot;) group.
          <br />
        </Indent>
        )
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      return (
        permissions.internalSystemAdministrator.hasPermissions(userProfile) ||
        isMemberOfAny(
          userProfile,
          ['rule_administrator'],
          isNotNull(constraints)
            ? constraints
            : securityGroupCheckConstraintEmpty,
        )
      );
    },
  },
  advancedManagement: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Role must be "System Administrator" or better.
        <br />
        OR
        <br />
        Organization has "Advanced Management" service.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
    ) => {
      const hasAccess =
        permissions.systemAdministrator.hasPermissions(userProfile) ||
        hasAnyActiveService(userProfile, ['advanced_management']);
      return hasAccess;
    },
  },
  uniFiService: {
    tooltip: (
      <>
        <b>Security: </b>
        <br />
        Organization has "UniFi" site configured.
        <br />
      </>
    ),
    hasPermissions: (
      userProfile: UserProfile,
      constraints?: SecurityGroupCheckConstraints,
      contractedServices?: ContractedServices,
    ) => {
      const hasAccess = OrganizationHasApiSiteType(
        contractedServices,
        'Ubiquiti Site',
      );
      return hasAccess;
    },
  },
};

export interface Permissions {
  events: Permission;
  eventManager: Permission;
  devices: Permission;
  assetInventory: Permission;
  continuity: Permission;
  software: Permission;
  softwareReporting: Permission;
  remoteAccess: Permission;
  remoteAccessAdmin: Permission;
  remoteAccessOverlayNetworkAdmin: Permission;
  remoteAccessConfigurationAdmin: Permission;
  ticketingInternal: Permission;
  ticketingViewer: Permission;
  ticketingUpdater: Permission;
  ticketingCreator: Permission;
  ticketingAdministrator: Permission;
  ibOrganization: Permission;
  internal: Permission;
  internalIBOrg: Permission;
  internalIBOrgContractManagement: Permission;
  internalRMM: Permission;
  systemAdministrator: Permission;
  internalSystemAdministrator: Permission;
  internalSystemAdministratorIBOrg: Permission;
  billingAdministratorIBOrg: Permission;
  organizationalAdministrator: Permission;
  internalOrganizationalAdministrator: Permission;
  vendor: Permission;
  collaborator: Permission;
  teamMember: Permission;
  optimize: Permission;
  rmm: Permission;
  survey: Permission;
  assessments: Permission;
  maintenanceWindowViewer: Permission;
  maintenanceWindowEditor: Permission;
  contractsEditor: Permission;
  contractsViewer: Permission;
  documentsAdministrator: Permission;
  threatDetectionConfigurationAdministrator: Permission;
  advancedManagement: Permission;
  configurationAdministrator: Permission;
  ruleAdministrator: Permission;
  ruleViewer: Permission;
  uniFiService: Permission;
}

export interface Permission {
  tooltip: ReactNode;
  hasPermissions: (
    userProfile: UserProfile,
    constraints?: SecurityGroupCheckConstraints,
    contractedServices?: ContractedServices,
  ) => boolean;
}
